import { CODE_SYSTEMS } from './constants';

export type SocialHistoryCoding = { system: string; code: string };
export type SocialHistoryTopic = SocialHistoryCoding & { display: string };

/**
 * The four topics a clinician records, one canonical code each. Written to `Observation.code` by the
 * add form, so the display is the LOINC one rather than a friendlier paraphrase.
 */
export const SOCIAL_HISTORY_TOPICS: readonly SocialHistoryTopic[] = [
  { system: CODE_SYSTEMS.LOINC, code: '72166-2', display: 'Tobacco smoking status' },
  { system: CODE_SYSTEMS.LOINC, code: '11331-6', display: 'History of alcohol use' },
  { system: CODE_SYSTEMS.LOINC, code: '11341-5', display: 'History of Occupation' },
  { system: CODE_SYSTEMS.LOINC, code: '11343-1', display: 'History of Other nonmedical drug use' },
];

/**
 * Every code that belongs to one of those topics, including the secondary statuses and the
 * quantities that imported charts carry. Reads filter on this rather than on `SOCIAL_HISTORY_TOPICS`
 * so an imported smokeless-tobacco status or drinks-per-day row is not dropped along with the
 * demographics, pregnancy status and screening instruments that share the social-history category.
 *
 * Epic's site-local free-text comment codes are deliberately absent: their system URI differs per
 * site, so a static list cannot match them.
 */
export const SOCIAL_HISTORY_TOPIC_CODES: readonly SocialHistoryCoding[] = [
  // Tobacco
  { system: CODE_SYSTEMS.LOINC, code: '72166-2' }, // Smoking status
  { system: CODE_SYSTEMS.LOINC, code: '88031-0' }, // Smokeless tobacco status
  { system: CODE_SYSTEMS.LOINC, code: '11367-0' }, // History of tobacco use
  { system: CODE_SYSTEMS.LOINC, code: '8663-7' }, // Cigarettes smoked per day
  { system: CODE_SYSTEMS.SNOMED, code: '401201003' }, // Cigarette pack-years
  // Alcohol
  { system: CODE_SYSTEMS.LOINC, code: '11331-6' }, // History of alcohol use
  { system: CODE_SYSTEMS.LOINC, code: '74013-4' }, // Alcoholic drinks per day
  { system: CODE_SYSTEMS.LOINC, code: '105992-2' }, // Alcoholic drinks per week
  // Occupation
  { system: CODE_SYSTEMS.LOINC, code: '11341-5' }, // History of occupation
  // Drugs
  { system: CODE_SYSTEMS.LOINC, code: '11343-1' }, // History of other nonmedical drug use
  { system: CODE_SYSTEMS.LOINC, code: '28355-6' }, // Substance use status
];

/**
 * The FHIR `code` search parameter value limiting a social-history search to those codes. FHIR ORs a
 * comma-separated token list, and each token is system-qualified so a bare code from another
 * terminology cannot match by accident.
 */
export const SOCIAL_HISTORY_CODE_SEARCH_PARAM = SOCIAL_HISTORY_TOPIC_CODES.map(
  ({ system, code }) => `${system}|${code}`,
).join(',');
